import { FC, useCallback, useState } from 'react'
import { useCreate, useNotify, useRedirect, useTranslate } from 'react-admin'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import { Edge, Node } from '@xyflow/react'
import WorkflowEditorPage from '../../pages/WorkflowEditor/WorkflowEditorPage'
import { edgesToRecord, nodesToRecord } from '../../utils/workflowMapping'

const WorkflowCreate: FC = () => {
  const [create] = useCreate()
  const notify = useNotify()
  const redirect = useRedirect()
  const translate = useTranslate()

  const [pending, setPending] = useState<{ nodes: Node[]; edges: Edge[] } | null>(null)
  const [nameDraft, setNameDraft] = useState('')

  const persist = useCallback(
    (name: string, nodes: Node[], edges: Edge[]) => {
      void create(
        'workflow',
        {
          data: {
            // eslint-disable-next-line camelcase
            display_name: name,
            nodes: nodesToRecord(nodes),
            edges: edgesToRecord(edges),
          },
        },
        {
          onSuccess: () => {
            notify('ra.notification.created', { type: 'info' })
            redirect('list', 'workflow')
          },
          onError: () => {
            notify('ra.notification.http_error', { type: 'error' })
          },
        },
      )
    },
    [create, notify, redirect],
  )

  const handleSave = useCallback((_name: string, nodes: Node[], edges: Edge[]) => {
    setNameDraft('')
    setPending({ nodes, edges })
  }, [])

  const handleConfirm = useCallback(() => {
    const trimmed = nameDraft.trim()
    if (!trimmed || !pending) return
    persist(trimmed, pending.nodes, pending.edges)
    setPending(null)
  }, [nameDraft, pending, persist])

  const handleClose = useCallback(() => setPending(null), [])

  return (
    <>
      <WorkflowEditorPage onSave={handleSave} />
      <Dialog open={pending !== null} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{translate('workflow.create.nameDialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={translate('workflow.create.nameLabel')}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleConfirm()
              }
            }}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{translate('ra.action.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!nameDraft.trim()}
          >
            {translate('ra.action.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default WorkflowCreate
